import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// ── Types ───────────────────────────────────────────────────────────────────────

export interface IProject {
  id: string;
  name: string;
  slug?: string;
}

interface ProjectContextValue {
  projects: IProject[];
  activeProjectId: string;
  setActiveProjectId: (id: string) => void;
  isLoading: boolean;
}

// ── Context ─────────────────────────────────────────────────────────────────────

const ProjectContext = createContext<ProjectContextValue>({
  projects: [],
  activeProjectId: '',
  setActiveProjectId: () => {},
  isLoading: true,
});

// ── Provider ────────────────────────────────────────────────────────────────────

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { token, user } = useAuth();

  const [projects, setProjects] = useState<IProject[]>([]);
  const [activeProjectId, setActiveProjectIdState] = useState<string>(() => {
    return localStorage.getItem('agnox:activeProjectId') ?? '';
  });
  const [isLoading, setIsLoading] = useState(true);

  const setActiveProjectId = useCallback((id: string) => {
    setActiveProjectIdState(id);
    if (id) {
      localStorage.setItem('agnox:activeProjectId', id);
    } else {
      localStorage.removeItem('agnox:activeProjectId');
    }
  }, []);

  useEffect(() => {
    if (!token || !user) {
      setIsLoading(false);
      return;
    }

    axios
      .get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data.success) {
          const fetched: IProject[] = res.data.projects ?? [];
          setProjects(fetched);

          // If stored activeProjectId no longer exists, default to first project
          const stored = localStorage.getItem('agnox:activeProjectId') ?? '';
          const valid = fetched.find((p) => p.id === stored);
          if (!valid && fetched.length > 0) {
            setActiveProjectId(fetched[0].id);
          }
        }
      })
      .catch(() => {
        // non-fatal — pages that need projects will handle their own fetch errors
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, user]);

  return (
    <ProjectContext.Provider value={{ projects, activeProjectId, setActiveProjectId, isLoading }}>
      {children}
    </ProjectContext.Provider>
  );
}

// ── Hook ────────────────────────────────────────────────────────────────────────

export function useProjectContext(): ProjectContextValue {
  return useContext(ProjectContext);
}
